import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { useEventReporter } from '~/event-reporter';
import { useIntegrations } from '~/hooks/reactQuery/queries/useIntegrations';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
import { IntegrationMappingModal } from '~/integrations/IntegrationMappingModal/IntegrationMappingModal';
import { integrationModel } from '~/models';
import { useStore, useStoreActions } from '~/store/store-context';
import { getAccountingDetails } from '../accounting.utils';
import { ACCOUNT_LINK_TYPES, buildAccountMappingRows } from './MapAccountsModal.utils';
export const GetMapAccountsModalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            MapAccountsModalTID: {
                Modal: (testid)=>testid ? "".concat(testid, "-map-accounts-modal") : '',
                BankRow: (testid)=>testid ? "".concat(testid, "-map-bank-account-row") : '',
                ExpenseRow: (testid)=>testid ? "".concat(testid, "-map-expense-account-row") : '',
                ClearingRow: (testid)=>testid ? "".concat(testid, "-map-clearing-account-row") : ''
            }
        }
    });
export function MapAccountsModal(param) {
    let { dismissModal, accounting } = param;
    var _ref, _ref1, _ref2;
    const { t: accountingTexts } = useLocaleKeys('integrations.accounting');
    const { api, logBI } = useStoreActions();
    const { integrations, refresh: refreshIntegrations } = useIntegrations({
        enabled: false
    });
    const { reportToHeap } = useEventReporter();
    const accountingIntegration = integrationModel.accountingIntegration(integrations, accounting);
    const [modalState, setModalState] = useState('loading');
    const [accountingAccounts, setAccountingAccounts] = useState();
    const userInfo = useStore((state)=>state.userInfo);
    const { testIdPrefix, biCodes, name: accountingName } = getAccountingDetails(accounting);
    const { MapAccountsModalTID } = useMemo(()=>GetMapAccountsModalTID(), []);
    useOnMount(()=>{
        fetchAccounts();
    });
    async function fetchAccounts() {
        setModalState('loading');
        try {
            const data = await api.accounting.getAccounts({
                accounting
            });
            setAccountingAccounts(data);
            setModalState('valid');
        } catch (err) {
            setModalState('error');
        }
    }
    async function onLink(linkType, account) {
        const isPaymentAccount = linkType !== 'expense';
        const biEventCode = isPaymentAccount ? biCodes.linkAccountToPaymentAccount : biCodes.linkAccount;
        logBI(biEventCode, {
            vendorId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId(),
            userId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()
        });
        const heapEventName = isPaymentAccount ? "".concat(testIdPrefix, "-select-payments-account") : "".concat(testIdPrefix, "-select-anchor-fees-account");
        reportToHeap('integrations', 'click', heapEventName);
        try {
            await api.accounting.linkAccount({
                accounting,
                data: {
                    linkType,
                    integratedId: account.id
                }
            });
            await refreshIntegrations({
                throwOnError: false
            });
        } catch (error) {
            sendErrReport('Failed to link service template', error);
            throw error;
        }
    }
    const rowMeta = {
        bank: {
            id: MapAccountsModalTID.BankRow(testIdPrefix),
            anchor: accountingTexts.mapAccountsModal.anchorAccountBank(),
            anchorInfo: accountingTexts.mapAccountsBankTooltip(),
            accounts: (_ref = accountingAccounts === null || accountingAccounts === void 0 ? void 0 : accountingAccounts.bank) !== null && _ref !== void 0 ? _ref : []
        },
        expense: {
            id: MapAccountsModalTID.ExpenseRow(testIdPrefix),
            anchor: accountingTexts.mapAccountsModal.anchorAccountExpense(),
            anchorInfo: accountingTexts.mapAccountsExpenseTooltip(),
            accounts: (_ref1 = accountingAccounts === null || accountingAccounts === void 0 ? void 0 : accountingAccounts.expense) !== null && _ref1 !== void 0 ? _ref1 : []
        },
        clearing: {
            id: MapAccountsModalTID.ClearingRow(testIdPrefix),
            anchor: accountingTexts.mapAccountsModal.anchorAccountClearing(),
            anchorInfo: accountingTexts.mapAccountsClearingTooltip(),
            accounts: (_ref2 = accountingAccounts === null || accountingAccounts === void 0 ? void 0 : accountingAccounts.clearing) !== null && _ref2 !== void 0 ? _ref2 : []
        }
    };
    const metaByRowId = new Map(ACCOUNT_LINK_TYPES.map((linkType)=>[
            rowMeta[linkType].id,
            {
                linkType,
                accounts: rowMeta[linkType].accounts
            }
        ]));
    const bankRowId = rowMeta.bank.id;
    const items = buildAccountMappingRows(rowMeta, accountingIntegration);
    const onMap = (param)=>{
        let { row, value } = param;
        const meta = metaByRowId.get(row.id);
        const account = meta === null || meta === void 0 ? void 0 : meta.accounts.find((option)=>option.id === value);
        if (meta == null || account == null) return;
        return onLink(meta.linkType, account);
    };
    return /*#__PURE__*/ _jsx(IntegrationMappingModal, {
        testId: MapAccountsModalTID.Modal(testIdPrefix),
        title: accountingTexts.mapAccountsModal.accountsHeader({
            accountingName: getAccountingDetails(accounting).fullName
        }),
        subheader: accountingTexts.mapAccountsSubheader(),
        state: modalState,
        items: items,
        anchorHeader: accountingTexts.mapAccountsModal.accountType(),
        select: {
            header: accountingTexts.mapAccountsModal.accountName(),
            selectBi: {
                name: 'map_account'
            },
            options: (row)=>{
                var _ref;
                var _metaByRowId_get;
                return ((_ref = (_metaByRowId_get = metaByRowId.get(row.id)) === null || _metaByRowId_get === void 0 ? void 0 : _metaByRowId_get.accounts) !== null && _ref !== void 0 ? _ref : []).map((account)=>({
                        value: account.id,
                        label: account.name
                    }));
            },
            emptySearchText: (row)=>row.id === bankRowId ? accountingTexts.mapAccountsModal.noOptionsTextBank() : accountingTexts.mapAccountsModal.noAccountsText({
                    accountingName
                }),
            placeholder: accountingTexts.mapAccountsModal.selectAccount(),
            disableClearable: true
        },
        onMap: onMap,
        onRefresh: fetchAccounts,
        errorMessage: accountingTexts.mapAccountsError(),
        closeBi: {
            name: 'map_accounts_modal_close'
        },
        onClose: dismissModal
    });
}
